import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'ecommerce');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = getArticlesByCategory(activeSlug);

  const baseUrl = 'https://fulidaohang.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${activeCategory.name} - 福利导航`,
        description: activeCategory.description,
        url: `${baseUrl}/category/${activeSlug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '福利导航',
          url: baseUrl,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '分类浏览',
            item: `${baseUrl}/category/ecommerce`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: activeCategory.name,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${activeCategory.name} - 福利导航`}
        description={activeCategory.description}
        keywords={`${activeCategory.name},福利导航,福利资源,优惠福利,${activeCategory.name}攻略`}
        canonicalUrl={`/category/${activeSlug}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '分类浏览', to: '/category/ecommerce' },
                { label: activeCategory.name },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mt-4 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {activeCategory.description}
            </p>
          </div>
        </div>

        <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.history.replaceState(null, '', `/category/${cat.slug}`);
                    window.scrollTo(0, 0);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}